import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useUser } from '@/api-hooks/useUser';
import Button from '@/components/v4/Button';
import { ModalXIconButton } from '@/components/v4/Modal/ModalPaper';
import { Review } from '@/types/review';
import { formatBusinessNumber } from '@/utils/format';

import { ArrowDownIcon, DocumentSignatureIcon, ReadOnlyInfoCard } from '../shared';

export const CpeatsBusinessConfirmContent = ({
  review,
  isRequesting,
  onConfirm,
  onClose,
}: {
  review: Review;
  isRequesting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) => {
  const { data: user } = useUser();
  const businessNumber = user?.business_number ? formatBusinessNumber(user.business_number) : '-';
  const storeId = review.store_id || '-';
  const orderNumber = review.platform_order_id || '-';
  const orderDate = review.created_at ? dayjs(review.created_at).format('YYYY년 MM월 DD일') : '-';

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [hasScrollableContent, setHasScrollableContent] = useState(false);

  const checkScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    setHasScrollableContent(scrollHeight > clientHeight);
    setIsScrolledToBottom(
      scrollHeight <= clientHeight || scrollHeight - scrollTop - clientHeight < 10,
    );
  }, []);

  useEffect(() => {
    setIsScrolledToBottom(false);
    setTimeout(checkScrollPosition, 100);
  }, [checkScrollPosition]);

  return (
    <div className="flex flex-col relative h-[80vh] max-h-[700px] md:h-[700px]">
      <div className="flex items-center justify-between px-5 py-2.5 mt-2.5 shrink-0">
        <p className="text-black font-semibold text-lg">리뷰 블라인드 신청 전 확인</p>
        <ModalXIconButton onClick={onClose} dataGtmId="blind-business-confirm-close" />
      </div>

      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto px-5 md:px-20 pb-40 pt-1 md:pt-2.5"
        onScroll={checkScrollPosition}
      >
        <div className="flex flex-col items-center mb-7">
          <DocumentSignatureIcon />
          <p className="text-cds-black-99 font-semibold text-lg leading-[150%] mt-2 text-center">
            아래 정보를 입력해서
          </p>
          <p className="text-cds-black-99 font-semibold text-lg leading-[150%] text-center">
            자동으로 신청할게요
          </p>
        </div>

        <div className="flex flex-col gap-2.5">
          <ReadOnlyInfoCard label="사업자번호" value={businessNumber} />
          <ReadOnlyInfoCard label="스토어 ID" value={storeId} />
          <ReadOnlyInfoCard label="주문번호" value={orderNumber} />
          <ReadOnlyInfoCard label="주문 일자" value={orderDate} />
          <ReadOnlyInfoCard label="리뷰 내용" value={review.content || '-'} multiline />
        </div>
        <div ref={bottomRef} />
      </div>

      <div className="absolute bottom-0 left-0 right-0 mx-5 md:mx-5">
        <div
          className="px-5 md:px-20"
          style={{
            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #FFF 90%)',
          }}
        >
          {hasScrollableContent && !isScrolledToBottom ? (
            <Button
              variant="contained"
              color="orange"
              className="w-full rounded-xl flex items-center justify-center gap-2"
              height={40}
              onClick={() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' })}
              data-gtm-id="blind-business-confirm-end"
            >
              끝까지 확인하기 <ArrowDownIcon />
            </Button>
          ) : (
            <div className="flex gap-2.5">
              <Button
                className="w-[88px] bg-cds-coolgray-10 text-cds-coolgray-80 rounded-xl shrink-0"
                height={40}
                onClick={onClose}
                data-gtm-id="blind-business-confirm-cancel"
              >
                취소
              </Button>
              <Button
                variant="contained"
                color="orange"
                className="flex-1 rounded-xl text-white"
                height={40}
                onClick={onConfirm}
                disabled={isRequesting}
                data-gtm-id="blind-business-confirm-submit"
              >
                {isRequesting ? (
                  <span className="flex items-center">
                    신청 중
                    <span className="loader-dots-2 ml-1.5" />
                  </span>
                ) : (
                  '확인했어요 (2/2) →'
                )}
              </Button>
            </div>
          )}
        </div>
        <div className="h-6 bg-white" />
      </div>
    </div>
  );
};
